import {
    $create,
    $getState,
    $setState,
    createState,
    DecoratorNode,
    type DOMConversionOutput,
    DOMExportOutput,
    type LexicalNode,
    SerializedLexicalNode,
    type Spread,
} from 'lexical'
import { ReactNode } from 'react'
import { KeysOfUnion } from 'type-fest'

import { MentionNodeComponent } from './MentionNodeComponent'
import { MENTIONS_TRIGGER_CHAR } from './MentionsPlugin'
import { MentionItem, mentionTypes } from './types'

/** Inspiration from  https://github.com/facebook/lexical/blob/main/packages/lexical-playground/src/nodes/MentionNode.ts
 * However, adapted to extend DecoratorNode instead of TextNode and using new NodeState API
 * See https://lexical.dev/docs/concepts/node-state
 */
export type SerializedMentionNode = Spread<MentionItem, SerializedLexicalNode>

const DEFAULT_MENTION: MentionItem = { id: '-1', value: '', type: 'user' }

function isMentionType(type: string | undefined): type is MentionItem['type'] {
    return type !== undefined && mentionTypes.includes(type)
}

export function normalizeMention(value: unknown): MentionItem {
    if (value && typeof value === 'object') {
        const candidate = value as Partial<MentionItem>
        const { id, value: mentionValue, type } = candidate

        if (typeof id === 'string' && typeof mentionValue === 'string' && isMentionType(type)) {
            if (type === 'practitioner') {
                return { id, value: mentionValue, type, fullname: candidate.fullname ?? mentionValue }
            }

            return { id, value: mentionValue, type }
        }
    }

    return { ...DEFAULT_MENTION }
}

const mentionState = createState('mention', {
    parse: (value: unknown): MentionItem => normalizeMention(value),
})

const mentionPropertyToAttribute: Record<KeysOfUnion<MentionItem>, string> = {
    id: 'data-lexical-mention-id',
    value: 'data-lexical-mention-value',
    type: 'data-lexical-mention-entity-type',
    fullname: 'data-lexical-mention-fullname',
}

export class MentionNode extends DecoratorNode<ReactNode> {
    $config() {
        return this.config('mention', {
            extends: DecoratorNode,
            stateConfigs: [{ stateConfig: mentionState, flat: true }],
        })
    }

    createDOM(): HTMLElement {
        return document.createElement('span')
    }

    updateDOM(): boolean {
        return false
    }

    toMentionItem(): MentionItem {
        return $getState(this, mentionState)
    }

    exportDOM(): DOMExportOutput {
        const mention = $getState(this, mentionState)
        const element = document.createElement('span')

        if ('id' in mention) {
            element.setAttribute(mentionPropertyToAttribute.id, mention.id.toString())
        }
        if ('value' in mention) {
            element.setAttribute(mentionPropertyToAttribute.value, mention.value)
        }
        if ('type' in mention) {
            element.setAttribute(mentionPropertyToAttribute.type, mention.type)
        }
        if ('fullname' in mention && mention.fullname) {
            element.setAttribute(mentionPropertyToAttribute.fullname, mention.fullname)
        }

        element.textContent = mention.value

        return { element }
    }

    static importDOM() {
        return {
            span: (domNode: HTMLElement) => {
                if (!domNode.hasAttribute(mentionPropertyToAttribute.value)) {
                    return null
                }
                return {
                    // eslint-disable-next-line @typescript-eslint/no-use-before-define
                    conversion: $convertMentionElement,
                    priority: 1,
                } as const
            },
        }
    }

    decorate(): ReactNode {
        return <MentionNodeComponent nodeKey={this.getKey()} mention={$getState(this, mentionState)} mentionCharacter={MENTIONS_TRIGGER_CHAR} />
    }

    getTextContent(): string {
        return `${MENTIONS_TRIGGER_CHAR}${$getState(this, mentionState).value}`
    }
}

export function $isMentionNode(node: LexicalNode | null | undefined): node is MentionNode {
    return node instanceof MentionNode
}

export function $createMentionNode(mention: MentionItem): MentionNode {
    return $setState($create(MentionNode), mentionState, mention)
}

function $convertMentionElement(domNode: HTMLElement): DOMConversionOutput | null {
    const mentionName = domNode.getAttribute(mentionPropertyToAttribute.value)
    const mentionId = domNode.getAttribute(mentionPropertyToAttribute.id)
    const mentionType = domNode.getAttribute(mentionPropertyToAttribute.type)

    if (mentionName !== null && mentionId !== null && mentionType !== null) {
        const mentionItem = normalizeMention({
            id: mentionId,
            value: mentionName,
            type: mentionType,
            fullname: domNode.getAttribute(mentionPropertyToAttribute.fullname) ?? undefined,
        })
        const node = $createMentionNode(mentionItem)
        return {
            node,
        }
    }

    return null
}
